// Lote Admin-V1 + V2 — SILLEDA_ADMIN panel with 5 tabs (Dashboard, Users,
// Settings, Announcements, KYC Review). KYC Review reuses the existing
// dedicated page component verbatim — we just embed it instead of routing
// to it. The Users tab lets admins block/unblock accounts; the Dashboard
// surfaces a per-role breakdown coming from the V2 /admin/stats payload.
//
// Access is gated client-side on `user.roles.includes('SILLEDA_ADMIN')`
// (multi-role aware, fix for B19 where the prior gate only checked
// `primary_role`). Server-side gating lives in
// `backend/src/routes/v1/admin.ts:isAdmin()`.

import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';

import { useAuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import Spinner from '../components/ui/Spinner';
import { describeAxiosError } from '../api/errors';
import {
  type AdminStats,
  type AdminUserRow,
  type AnnouncementRow,
  type AnnouncementTargetRole,
  type PlatformSettingItem,
  blockUser,
  createAnnouncement,
  getAdminStats,
  getPitchTokenUrl,
  listAdminUsers,
  listAnnouncements,
  listPlatformSettings,
  unblockUser,
  updatePlatformSetting,
} from '../api/admin';
import { elevate2fa, elevationStatus2fa } from '../api/profile';

import AdminKycReviewPage from './AdminKycReviewPage';
import InvoicingTab from './admin/InvoicingTab';
import DisputesTab from './admin/DisputesTab';

type TabKey =
  | 'dashboard'
  | 'users'
  | 'settings'
  | 'announcements'
  | 'kyc'
  | 'invoicing'
  | 'disputes';

const TAB_LABELS: Record<TabKey, string> = {
  dashboard: 'Resumo',
  users: 'Usuarios',
  settings: 'Configuración',
  announcements: 'Anuncios',
  kyc: 'Revisión KYC',
  invoicing: 'Facturación',
  disputes: 'Disputas',
};

const TAB_ORDER: TabKey[] = [
  'dashboard',
  'users',
  'settings',
  'announcements',
  'kyc',
  'invoicing',
  'disputes',
];

export default function AdminPage(): JSX.Element {
  const { user } = useAuthContext();
  const [params, setParams] = useSearchParams();

  const rawTab = params.get('tab');
  const tab: TabKey = (TAB_ORDER as string[]).includes(rawTab ?? '')
    ? (rawTab as TabKey)
    : 'dashboard';

  if (!user) return <Spinner />;
  // B19 fix — admin can be a secondary role. The prior gate at
  // `primary_role === 'SILLEDA_ADMIN'` locked out anyone holding admin as a
  // non-primary assignment.
  if (!user.roles.includes('SILLEDA_ADMIN')) {
    return <Navigate to="/marketplace" replace />;
  }

  function switchTab(next: TabKey): void {
    setParams({ tab: next }, { replace: true });
  }

  return (
    <main className="max-w-5xl mx-auto px-6 py-10">
      <header className="mb-6">
        <h1 className="text-3xl font-extrabold text-dark">Panel de administración</h1>
        <p className="mt-2 text-sm text-muted">Configuración global, anuncios e revisión KYC.</p>
      </header>

      <ElevationGate>
        <nav className="border-b border-border flex gap-1" role="tablist">
          {TAB_ORDER.map((key) => {
            const active = key === tab;
            return (
              <button
                key={key}
                role="tab"
                aria-selected={active}
                onClick={() => switchTab(key)}
                className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
                  active
                    ? 'border-rauxa text-rauxa'
                    : 'border-transparent text-muted hover:text-dark'
                }`}
              >
                {TAB_LABELS[key]}
              </button>
            );
          })}
        </nav>

        <section className="mt-6" role="tabpanel">
          {tab === 'dashboard' ? <DashboardTab /> : null}
          {tab === 'users' ? <UsersTab adminUserId={user.id} /> : null}
          {tab === 'settings' ? <SettingsTab /> : null}
          {tab === 'announcements' ? <AnnouncementsTab /> : null}
          {tab === 'kyc' ? <AdminKycReviewPage /> : null}
          {tab === 'invoicing' ? <InvoicingTab /> : null}
          {tab === 'disputes' ? <DisputesTab /> : null}
        </section>
      </ElevationGate>
    </main>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Lote V-13 — Elevation gate (2FA step-up). Mirrors Leyro pattern.
//
// State machine:
//   - status='checking' → blocking spinner while GET /auth/2fa/elevation-status
//   - !totpEnabled        → banner asking user to activate 2FA in Perfil
//   - totpEnabled && !elevated → TOTP input form
//   - elevated            → render children (tabs)
// ──────────────────────────────────────────────────────────────────────────

interface ElevationState {
  status: 'checking' | 'needs-2fa' | 'needs-code' | 'elevated' | 'error';
  errorMsg?: string;
}

function ElevationGate({ children }: { children: React.ReactNode }): JSX.Element {
  const [state, setState] = useState<ElevationState>({ status: 'checking' });

  const refresh = useCallback(async () => {
    try {
      const data = await elevationStatus2fa();
      if (!data.totpEnabled) {
        setState({ status: 'needs-2fa' });
        return;
      }
      setState({ status: data.elevated ? 'elevated' : 'needs-code' });
    } catch (err) {
      setState({
        status: 'error',
        errorMsg: describeAxiosError(err, 'erro descoñecido'),
      });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (state.status === 'checking') {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="border border-red-200 bg-red-50/60 rounded-2xl p-6 max-w-lg">
        <p className="text-sm text-red-700">
          Non se puido comprobar a elevación de sesión: {state.errorMsg}
        </p>
        <button
          type="button"
          onClick={() => void refresh()}
          className="mt-3 px-3 py-1.5 text-sm font-semibold border border-red-300 rounded-lg hover:bg-red-100"
        >
          Volver intentar
        </button>
      </div>
    );
  }

  if (state.status === 'needs-2fa') {
    return (
      <div className="border border-amber-200 bg-amber-50/50 rounded-2xl p-6 max-w-lg">
        <div className="flex items-start gap-3 mb-3">
          <ShieldCheck size={22} className="text-amber-600 shrink-0 mt-0.5" />
          <div>
            <h3 className="font-bold text-dark">Activa 2FA para acceder ao panel</h3>
            <p className="text-sm text-muted mt-1">
              O panel de administración esixe verificación en dúas etapas. Activa 2FA no teu perfil
              e volve aquí.
            </p>
          </div>
        </div>
        <Link
          to="/marketplace/profile#conta"
          className="inline-block px-4 py-2 bg-dark text-white rounded-lg text-sm font-semibold"
        >
          Ir ao perfil
        </Link>
      </div>
    );
  }

  if (state.status === 'needs-code') {
    return <TotpChallengeForm onSuccess={refresh} />;
  }

  return <>{children}</>;
}

function TotpChallengeForm({ onSuccess }: { onSuccess: () => Promise<void> }): JSX.Element {
  const { addToast } = useToast();
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!/^\d{6,8}$/.test(code.trim())) {
      setError('Introduce o código de 6 díxitos da túa app TOTP (ou un código de respaldo).');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await elevate2fa(code.trim());
      addToast('Sesión elevada por 30 minutos.', 'default');
      setCode('');
      await onSuccess();
    } catch (err) {
      setError(describeAxiosError(err, 'Código incorrecto ou caducado.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="border border-border bg-white rounded-2xl p-6 max-w-lg">
      <div className="flex items-start gap-3 mb-4">
        <ShieldCheck size={22} className="text-rauxa shrink-0 mt-0.5" />
        <div>
          <h3 className="font-bold text-dark">Confirma a túa identidade</h3>
          <p className="text-sm text-muted mt-1">
            Introduce o código de 6 díxitos da túa app autenticadora. A sesión admin manterase
            activa 30 minutos.
          </p>
        </div>
      </div>
      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-3">
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\s/g, ''))}
          placeholder="123456"
          className="w-full px-4 py-3 text-center text-2xl tracking-widest font-mono border border-border rounded-lg focus:outline-none focus:border-rauxa"
          maxLength={8}
          disabled={submitting}
        />
        {error ? <p className="text-sm text-red-600">{error}</p> : null}
        <button
          type="submit"
          disabled={submitting || code.trim().length === 0}
          className="w-full px-4 py-3 bg-rauxa text-white rounded-lg text-sm font-semibold disabled:opacity-60"
        >
          {submitting ? 'Verificando…' : 'Acceder ao panel'}
        </button>
      </form>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Dashboard tab — KPIs from /admin/stats
// ──────────────────────────────────────────────────────────────────────────

function DashboardTab(): JSX.Element {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const s = await getAdminStats();
        if (alive) setStats(s);
      } catch (e) {
        if (alive) setError(describeAxiosError(e, 'Non se puido cargar as estatísticas.'));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (loading) return <Spinner />;
  if (error) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {error}
      </div>
    );
  }
  if (!stats) return <></>;

  const KPI = ({ label, value }: { label: string; value: number }): JSX.Element => (
    <div className="rounded-lg border border-border bg-surface p-5 shadow-sm">
      <div className="text-xs text-muted uppercase tracking-wide">{label}</div>
      <div className="mt-2 text-3xl font-extrabold text-dark">{value.toLocaleString('gl')}</div>
    </div>
  );

  const roleEntries: Array<[keyof AdminStats['users_by_role'], string]> = [
    ['FARMER', 'Gandeiros'],
    ['VET', 'Veterinarios'],
    ['BUYER', 'Compradores'],
    ['TRANSPORTISTA', 'Transportistas'],
    ['INDUSTRY', 'Industria'],
    ['SILLEDA_ADMIN', 'Administradores'],
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPI label="Usuarios" value={stats.users} />
        <KPI label="Granxas" value={stats.farms} />
        <KPI label="Animais" value={stats.animals} />
        <KPI label="Eventos" value={stats.events} />
        <KPI label="Subastas activas" value={stats.auctions_listed} />
        <KPI label="Anclaxes Merkle" value={stats.merkle_anchors} />
      </div>

      <div className="rounded-lg border border-border bg-surface p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-dark mb-3">Usuarios por rol</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {roleEntries.map(([key, label]) => (
            <div key={key} className="flex items-baseline justify-between">
              <span className="text-xs text-muted">{label}</span>
              <span className="text-lg font-bold text-dark">
                {stats.users_by_role[key].toLocaleString('gl')}
              </span>
            </div>
          ))}
        </div>
        {stats.users_blocked > 0 ? (
          <p className="mt-3 text-xs text-red-700">
            {stats.users_blocked} usuario{stats.users_blocked === 1 ? '' : 's'} bloqueado
            {stats.users_blocked === 1 ? '' : 's'}.
          </p>
        ) : null}
      </div>

      <PitchDeckButton />
    </div>
  );
}

// Lote V-15 — Botón que xera un URL temporal e abre o pitch deck privado.
// Esixe SILLEDA_ADMIN + elevation 2FA (xa pasaches ese gate para chegar
// aquí). O token caduca en 30 min; se queres mostrar de novo despois,
// volve a clicar.
function PitchDeckButton(): JSX.Element {
  const { addToast } = useToast();
  const [busy, setBusy] = useState(false);

  async function openPitch(): Promise<void> {
    if (busy) return;
    setBusy(true);
    try {
      const { url } = await getPitchTokenUrl();
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      addToast(describeAxiosError(e, 'Non se puido xerar o enlace do pitch.'), 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-lg border border-border bg-surface p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-dark">Pitch deck comercial</h3>
          <p className="mt-1 text-xs text-muted">
            Presentación privada para reunións. Ábrese nunha pestana nova. O enlace caduca aos 30
            minutos.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void openPitch()}
          disabled={busy}
          className="px-4 py-2 rounded-md bg-rauxa text-white text-sm font-semibold disabled:opacity-60"
        >
          {busy ? 'Xerando…' : 'Abrir pitch →'}
        </button>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Users tab — list + block/unblock (Lote Admin-V2)
// ──────────────────────────────────────────────────────────────────────────
// We render the 50 most recent users; the table is read-only except for
// the per-row "Bloquear"/"Desbloquear" buttons. Blocking opens an inline
// reason input that the backend persists into `block_reason` and shows to
// the affected user the next time they try to log in. Self-block is gated
// both client-side (button hidden for own row) and server-side (400).

interface RejectDraft {
  userId: string;
  reason: string;
}

function UsersTab({ adminUserId }: { adminUserId: string }): JSX.Element {
  const { addToast } = useToast();
  const [rows, setRows] = useState<AdminUserRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [draft, setDraft] = useState<RejectDraft | null>(null);

  async function reload(): Promise<void> {
    setError(null);
    try {
      setRows(await listAdminUsers(50));
    } catch (e) {
      setError(describeAxiosError(e, 'Non se puido cargar a lista de usuarios.'));
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  async function doBlock(): Promise<void> {
    if (!draft || draft.reason.trim().length < 5) return;
    setBusy(draft.userId);
    try {
      await blockUser(draft.userId, draft.reason.trim());
      addToast('Usuario bloqueado.', 'success');
      setDraft(null);
      await reload();
    } catch (e) {
      addToast(describeAxiosError(e, 'Non se puido bloquear.'), 'error');
    } finally {
      setBusy(null);
    }
  }

  async function doUnblock(userId: string): Promise<void> {
    setBusy(userId);
    try {
      await unblockUser(userId);
      addToast('Usuario desbloqueado.', 'success');
      await reload();
    } catch (e) {
      addToast(describeAxiosError(e, 'Non se puido desbloquear.'), 'error');
    } finally {
      setBusy(null);
    }
  }

  if (!rows) {
    return error ? (
      <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {error}
      </div>
    ) : (
      <Spinner />
    );
  }

  return (
    <div>
      {error ? (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}
      <ul className="space-y-2">
        {rows.map((row) => {
          const isSelf = row.id === adminUserId;
          const isBlocked = row.blocked_at !== null;
          const isBusy = busy === row.id;
          const isDrafting = draft?.userId === row.id;
          return (
            <li
              key={row.id}
              className={`rounded-md border bg-surface p-4 shadow-sm ${
                isBlocked ? 'border-red-300' : 'border-border'
              }`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-base font-semibold text-dark">{row.name}</div>
                  <div className="text-xs text-muted mt-0.5">
                    {row.email ?? '—'} · {row.roles.join(', ')}
                  </div>
                  {isBlocked ? (
                    <div className="mt-1 text-xs text-red-700">
                      Bloqueado{' '}
                      {row.blocked_at ? new Date(row.blocked_at).toLocaleDateString('gl') : ''}
                      {row.block_reason ? ` — ${row.block_reason}` : ''}
                    </div>
                  ) : null}
                </div>
                <div className="flex gap-2">
                  {isSelf ? (
                    <span className="text-xs text-muted self-center">(ti)</span>
                  ) : isBlocked ? (
                    <button
                      type="button"
                      disabled={isBusy}
                      onClick={() => void doUnblock(row.id)}
                      className="px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-semibold disabled:opacity-50"
                    >
                      {isBusy ? 'Desbloqueando…' : 'Desbloquear'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={isBusy}
                      onClick={() => setDraft({ userId: row.id, reason: '' })}
                      className="px-3 py-1.5 rounded-md border border-red-300 text-red-700 text-xs font-semibold disabled:opacity-50"
                    >
                      Bloquear
                    </button>
                  )}
                </div>
              </div>

              {isDrafting && draft ? (
                <div className="mt-3 space-y-2">
                  <label className="block text-xs text-muted">
                    Motivo do bloqueo (mín. 5 caracteres, visible para o usuario):
                  </label>
                  <textarea
                    value={draft.reason}
                    onChange={(e) => setDraft({ ...draft, reason: e.target.value })}
                    rows={2}
                    maxLength={500}
                    className="w-full rounded-md border border-border bg-white p-2 text-sm"
                    placeholder="Ex.: suspeita de fraude no marketplace — pendente de verificación."
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setDraft(null)}
                      className="px-3 py-1.5 rounded-md border border-border text-xs"
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      disabled={isBusy || draft.reason.trim().length < 5}
                      onClick={() => void doBlock()}
                      className="px-3 py-1.5 rounded-md bg-red-600 text-white text-xs font-semibold disabled:opacity-50"
                    >
                      {isBusy ? 'Bloqueando…' : 'Confirmar bloqueo'}
                    </button>
                  </div>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Settings tab — runtime feature flags
// ──────────────────────────────────────────────────────────────────────────

function SettingsTab(): JSX.Element {
  const { addToast } = useToast();
  const [items, setItems] = useState<PlatformSettingItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);

  async function reload(): Promise<void> {
    setError(null);
    try {
      setItems(await listPlatformSettings());
    } catch (e) {
      setError(describeAxiosError(e, 'Non se puido cargar a configuración.'));
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  async function toggle(key: PlatformSettingItem['key'], next: boolean): Promise<void> {
    setSavingKey(key);
    try {
      const updated = await updatePlatformSetting(key, next);
      setItems((prev) =>
        (prev ?? []).map((it) => (it.key === key ? { ...it, value: updated.value } : it)),
      );
      addToast('Configuración actualizada.', 'success');
    } catch (e) {
      addToast(describeAxiosError(e, 'Non se puido gardar.'), 'error');
    } finally {
      setSavingKey(null);
    }
  }

  if (!items) {
    return error ? (
      <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {error}
      </div>
    ) : (
      <Spinner />
    );
  }

  const registrationOpen = items.find((i) => i.key === 'registration_open');
  const privateBeta = items.find((i) => i.key === 'private_beta_mode');

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-surface p-5 shadow-sm">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-sm font-semibold text-dark">Rexistro aberto</div>
            <p className="mt-1 text-xs text-muted">
              Cando está desactivado, os novos usuarios non poden crear conta. Os emails
              configurados como administradores sempre poden entrar.
            </p>
          </div>
          <button
            type="button"
            disabled={savingKey === 'registration_open'}
            onClick={() => void toggle('registration_open', !(registrationOpen?.value === true))}
            className={`shrink-0 px-4 py-2 rounded-md text-sm font-semibold border ${
              registrationOpen?.value === true
                ? 'bg-emerald-600 text-white border-emerald-600'
                : 'bg-surface text-dark border-border'
            } disabled:opacity-50`}
            aria-pressed={registrationOpen?.value === true}
          >
            {savingKey === 'registration_open'
              ? 'Gardando…'
              : registrationOpen?.value === true
                ? 'Aberto'
                : 'Pechado'}
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-surface p-5 shadow-sm">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-sm font-semibold text-dark">Modo Beta Privado</div>
            <p className="mt-1 text-xs text-muted">
              Cando está activado: <strong>bloquéase o rexistro público</strong>, e o marketplace{' '}
              <strong>non mostra animais</strong> a usuarios non autenticados. A web mostra unha
              landing especial de "modo beta privado". Os usuarios xa rexistrados seguen accedendo a
              todo con normalidade.
            </p>
          </div>
          <button
            type="button"
            disabled={savingKey === 'private_beta_mode'}
            onClick={() => void toggle('private_beta_mode', !(privateBeta?.value === true))}
            className={`shrink-0 px-4 py-2 rounded-md text-sm font-semibold border ${
              privateBeta?.value === true
                ? 'bg-amber-600 text-white border-amber-600'
                : 'bg-surface text-dark border-border'
            } disabled:opacity-50`}
            aria-pressed={privateBeta?.value === true}
          >
            {savingKey === 'private_beta_mode'
              ? 'Gardando…'
              : privateBeta?.value === true
                ? 'Activo'
                : 'Inactivo'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Announcements tab — compose form + history list
// ──────────────────────────────────────────────────────────────────────────

const TARGET_ROLE_LABELS: Record<AnnouncementTargetRole | 'ALL', string> = {
  ALL: 'Todos os usuarios',
  FARMER: 'Só gandeiros',
  VET: 'Só veterinarios',
  BUYER: 'Só compradores',
  TRANSPORTISTA: 'Só transportistas',
  INDUSTRY: 'Só industria',
  SILLEDA_ADMIN: 'Só administradores',
};

function AnnouncementsTab(): JSX.Element {
  const { addToast } = useToast();
  const [rows, setRows] = useState<AnnouncementRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [target, setTarget] = useState<AnnouncementTargetRole | 'ALL'>('ALL');
  const [sending, setSending] = useState(false);

  async function reload(): Promise<void> {
    setError(null);
    try {
      setRows(await listAnnouncements(50));
    } catch (e) {
      setError(describeAxiosError(e, 'Non se puido cargar o histórico.'));
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  async function submit(): Promise<void> {
    if (title.trim().length === 0 || body.trim().length === 0) return;
    setSending(true);
    try {
      const created = await createAnnouncement({
        title: title.trim(),
        body: body.trim(),
        target_role: target === 'ALL' ? null : target,
      });
      addToast(`Anuncio enviado a ${created.recipients_count} usuarios.`, 'success');
      setTitle('');
      setBody('');
      setTarget('ALL');
      await reload();
    } catch (e) {
      addToast(describeAxiosError(e, 'Non se puido enviar.'), 'error');
    } finally {
      setSending(false);
    }
  }

  const canSubmit = title.trim().length > 0 && body.trim().length > 0 && !sending;

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-border bg-surface p-5 shadow-sm">
        <h2 className="text-base font-semibold text-dark mb-3">Enviar novo anuncio</h2>
        <div className="space-y-3">
          <label className="block">
            <span className="text-xs text-muted">Título</span>
            <input
              type="text"
              value={title}
              maxLength={120}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1 w-full rounded-md border border-border bg-white p-2 text-sm"
              placeholder="Ex.: Mantemento programado"
            />
          </label>
          <label className="block">
            <span className="text-xs text-muted">Corpo do anuncio</span>
            <textarea
              value={body}
              maxLength={2000}
              rows={4}
              onChange={(e) => setBody(e.target.value)}
              className="mt-1 w-full rounded-md border border-border bg-white p-2 text-sm"
              placeholder="Texto que recibirán os usuarios na notificación push."
            />
          </label>
          <label className="block">
            <span className="text-xs text-muted">Destinatarios</span>
            <select
              value={target}
              onChange={(e) => setTarget(e.target.value as AnnouncementTargetRole | 'ALL')}
              className="mt-1 w-full rounded-md border border-border bg-white p-2 text-sm"
            >
              {Object.entries(TARGET_ROLE_LABELS).map(([k, label]) => (
                <option key={k} value={k}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <div className="flex justify-end">
            <button
              type="button"
              disabled={!canSubmit}
              onClick={() => void submit()}
              className="px-4 py-2 rounded-md bg-rauxa text-white text-sm font-semibold disabled:opacity-50"
            >
              {sending ? 'Enviando…' : 'Enviar anuncio'}
            </button>
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-base font-semibold text-dark mb-3">Histórico</h2>
        {error ? (
          <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error}
          </div>
        ) : !rows ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <div className="rounded-md border border-border bg-surface p-6 text-sm text-muted text-center">
            Aínda non se enviou ningún anuncio.
          </div>
        ) : (
          <ul className="space-y-2">
            {rows.map((row) => (
              <li key={row.id} className="rounded-md border border-border bg-surface p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="font-semibold text-dark">{row.title}</div>
                  <div className="text-xs text-muted whitespace-nowrap">
                    {new Date(row.sent_at).toLocaleString('gl')}
                  </div>
                </div>
                <div className="mt-1 text-sm text-dark whitespace-pre-wrap">{row.body}</div>
                <div className="mt-2 text-xs text-muted">
                  {row.target_role ? `Rol ${row.target_role}` : 'Todos os usuarios'} ·{' '}
                  {row.recipients_count} destinatarios · {row.sent_by}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
